import React from 'react';
import { Link } from 'react-router-dom';
import { translationsRootKey } from '../../../settings';
import { t } from '../../../i18n';
import { isMenuItemHidden } from '../options';
import { ArrowLeftIcon } from '../../../assets/icons/sidebar';

// Creates menu item content with icon and label
const getMenuItemContent = (IconComponent, labelKey) => {
  return (
    <span className="isoMenuHolder">
      {IconComponent && <IconComponent />}
      <span className="nav-text">{t(`${translationsRootKey}.${labelKey}`)}</span>
    </span>
  );
};

// Transforms a menu option into Ant Design Menu item format
export const getMenuItem = (singleOption, handlers, menuIcons) => {
  const { key, label, children, target, dataCy, isSettingsToggle } = singleOption;
  const { onMenuItemClick, auth, permissions } = handlers;
  const IconComponent = menuIcons[key];

  let childrenItems = null;

  if (children) {
    childrenItems = {
      children: children
        .filter(item => !isMenuItemHidden(item, auth.role, permissions))
        .map(child => {
          return {
            key: child.key,
            'data-cy': child.dataCy,
            label: (
              <Link
                target={child.target}
                to={child.key}
                disabled={child.disabled}
                onClick={onMenuItemClick}
                data-cy="sidebar-menu_item-child_link"
              >
                {t(`${translationsRootKey}.${child.label}`)}
              </Link>
            ),
          };
        }),
    };
  }

  let labelContent;

  if (isSettingsToggle) {
    labelContent = <div data-cy="sidebar-menu_item-link">{getMenuItemContent(IconComponent, label)}</div>;
  } else if (children) {
    labelContent = <div key={key}>{getMenuItemContent(IconComponent, label)}</div>;
  } else {
    labelContent = (
      <Link to={key} target={target} data-cy="sidebar-menu_item-link">
        {getMenuItemContent(IconComponent, label)}
      </Link>
    );
  }

  return {
    key,
    'data-cy': dataCy,
    // Collapsed, the menu builds its tooltip from the label — here the whole holder, icon included.
    title: t(`${translationsRootKey}.${label}`),
    label: labelContent,
    ...childrenItems,
  };
};

// Generates a settings menu item
const getSettingsMenuItem = (item, settingsIcons, onMenuItemClick) => {
  const IconComponent = settingsIcons[item.key];
  const label = t(`${translationsRootKey}.${item.label}`);

  return {
    key: item.key,
    'data-cy': item.dataCy,
    // Named outright: collapsed, the menu builds its tooltip from the label's text, which would read the
    // "New" tag as part of the name.
    title: label,
    label: (
      <Link to={item.key} target={item.target} onClick={onMenuItemClick} data-cy="sidebar-menu_item-link">
        <span className="isoMenuHolder">
          {IconComponent && <IconComponent />}
          <span className="nav-text">{label}</span>
          {item.isNew && <span className="nav-tag-new">{t(`${translationsRootKey}.sidebar.tag_new`)}</span>}
        </span>
      </Link>
    ),
  };
};

// Generates all settings menu items including back button and grouped items
export const getSettingsMenuItems = (settingsMenuOptions, auth, settingsIcons, onMenuItemClick, permissions = null) => {
  const backToAppItem = {
    key: 'back-to-app',
    'data-cy': 'settings-back-to-app',
    label: (
      <span className="isoMenuHolder" data-cy="sidebar-menu_item-link">
        <ArrowLeftIcon />
        <span className="nav-text">{t('dash.sidebar.settings_back_to_app')}</span>
      </span>
    ),
  };

  const categoryItems = settingsMenuOptions
    .map(category => {
      const visibleItems = category.items.filter(item => !isMenuItemHidden(item, auth.role, permissions));

      if (visibleItems.length === 0) {
        return null;
      }

      return {
        key: category.key,
        type: 'group',
        label: t(category.label),
        children: visibleItems.map(item => getSettingsMenuItem(item, settingsIcons, onMenuItemClick)),
      };
    })
    .filter(Boolean);

  return [backToAppItem, ...categoryItems];
};
